/* oxlint-disable tailwindcss/no-unknown-classes -- Styles are colocated in home-new.css. */
import { Brain, Code, Search, Wrench, type LucideIcon } from "lucide-react"
import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react"

import { agents, type AgentConfig } from "@/lib/agents"

import { elevatedRingClassName } from "./homeNewStyles"
import { teamAvatars } from "./teamAvatars"
import { TrailsIcon } from "./TrailsIcon"
import { useRevealOnce } from "./useRevealOnce"

// Session Storage showcase: one of Peyton's agent sessions from the billing
// work, and three products below it that each pick up a different piece of
// it. A dashed link runs from the piece to the product that uses it: the
// prompt to Agentic Search, the changed files to the semantic graph, and
// Peyton's decision to Trails. The links are the code hosting scene's white
// dashes and run behind the cards, so they leave the session at its edges. Links are measured from the live layout so the
// scene reflows with the panel instead of scaling; narrow panels (below the
// `@[34rem]` container width) keep only the prompt, the decision and the two
// products they link to, so the scene fits the shorter phone panel. Styles:
// `.home-new-session-links` in home-new.css.

type LinkKey = "search" | "graph" | "trails"

const PRODUCTS: Array<{
  key: LinkKey
  title: string
  Icon: LucideIcon | typeof TrailsIcon
  body: string
}> = [
  {
    key: "trails",
    title: "Trails",
    Icon: TrailsIcon,
    body: "Turns “never overcharge” into an eval every billing change has to pass.",
  },
  {
    key: "graph",
    title: "Semantic graph",
    Icon: Brain,
    body: "Links plan.ts and invoice.ts to proration, so the next agent reads only those.",
  },
  {
    key: "search",
    title: "Agentic Search",
    Icon: Search,
    body: "Finds this session when anyone asks why seats bill mid-year.",
  },
]

/** How far the side links run outside the session card (px). */
const OUTSET = 14

export interface Point {
  x: number
  y: number
}

/** An orthogonal polyline through `points` with rounded 8px corners. */
export function roundedPath(points: Point[]) {
  const [first, ...rest] = points
  if (!first) return ""
  const parts = [`M ${first.x} ${first.y}`]
  rest.forEach((point, index) => {
    const next = rest[index + 1]
    if (!next) {
      parts.push(`L ${point.x} ${point.y}`)
      return
    }
    const previous = index === 0 ? first : rest[index - 1]!
    const radius = Math.min(
      8,
      Math.hypot(point.x - previous.x, point.y - previous.y) / 2,
      Math.hypot(next.x - point.x, next.y - point.y) / 2,
    )
    const toward = (from: Point, to: Point, distance: number) => {
      const length = Math.hypot(to.x - from.x, to.y - from.y) || 1
      return {
        x: from.x + ((to.x - from.x) / length) * distance,
        y: from.y + ((to.y - from.y) / length) * distance,
      }
    }
    const start = toward(point, previous, radius)
    const end = toward(point, next, radius)
    parts.push(`L ${start.x} ${start.y}`, `Q ${point.x} ${point.y} ${end.x} ${end.y}`)
  })
  return parts.join(" ")
}

interface Geometry {
  width: number
  height: number
  links: Array<{ key: LinkKey; d: string }>
}

/** Where a run of text ends: the right edge and middle of its last line box. */
function textEnd(element: HTMLElement, base: DOMRect): Point | null {
  const lines = element.getClientRects()
  const last = lines[lines.length - 1]
  if (!last || last.width === 0) return null
  return { x: last.right - base.left, y: last.top + last.height / 2 - base.top }
}

/** Measures the three links whenever the scene resizes. */
function useLinks(
  scene: RefObject<HTMLDivElement | null>,
  session: RefObject<HTMLDivElement | null>,
  anchors: RefObject<Partial<Record<LinkKey, HTMLElement | null>>>,
  cards: RefObject<Partial<Record<LinkKey, HTMLElement | null>>>,
) {
  const [geometry, setGeometry] = useState<Geometry | null>(null)

  useLayoutEffect(() => {
    const sceneElement = scene.current
    if (!sceneElement) return
    const measure = () => {
      const base = sceneElement.getBoundingClientRect()
      const sessionRect = session.current?.getBoundingClientRect()
      if (!sessionRect) return
      const left = sessionRect.left - base.left
      const right = sessionRect.right - base.left
      const links: Geometry["links"] = []

      for (const key of ["search", "graph", "trails"] as const) {
        const anchor = anchors.current[key]
        const cardRect = cards.current[key]?.getBoundingClientRect()
        if (!anchor || !cardRect || cardRect.width === 0) continue
        const cardTop = cardRect.top - base.top
        const cardX = cardRect.left - base.left + cardRect.width / 2
        // The gap between the session card and the products.
        const gapY = (sessionRect.bottom - base.top + cardTop) / 2

        if (key === "trails") {
          // From Peyton's avatar out past the card's left edge, then over and
          // down into Trails.
          const rect = anchor.getBoundingClientRect()
          const start = { x: rect.left - base.left - 4, y: rect.top + rect.height / 2 - base.top }
          links.push({
            key,
            d: roundedPath([
              start,
              { x: left - OUTSET, y: start.y },
              { x: left - OUTSET, y: gapY },
              { x: cardX, y: gapY },
              { x: cardX, y: cardTop },
            ]),
          })
          continue
        }

        const end = textEnd(anchor, base)
        if (!end) continue
        const start = { x: end.x + 8, y: end.y }
        links.push({
          key,
          d:
            key === "search"
              ? // From the prompt out past the card's right edge, then back
                // under the card and down into Search.
                roundedPath([
                  start,
                  { x: right + OUTSET, y: start.y },
                  { x: right + OUTSET, y: gapY },
                  { x: cardX, y: gapY },
                  { x: cardX, y: cardTop },
                ])
              : // Straight over from the changed files, then down into the graph.
                roundedPath([start, { x: cardX, y: start.y }, { x: cardX, y: cardTop }]),
        })
      }

      setGeometry({ width: base.width, height: base.height, links })
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(sceneElement)
    return () => observer.disconnect()
  }, [scene, session, anchors, cards])

  return geometry
}

/** A teammate's round photo; the white ring sets it apart where it overlaps an agent mark. */
export function PersonAvatar({
  src = teamAvatars.peyton,
  ringed = true,
}: {
  src?: string
  ringed?: boolean
}) {
  return (
    <img
      src={src}
      alt=""
      width={24}
      height={24}
      loading="lazy"
      className={`size-6 shrink-0 rounded-full object-cover ${ringed ? "border-2 border-white dark:border-(--neutral-200)" : ""}`}
    />
  )
}

/** An agent's logo in a round well, the size of a person avatar. */
export function AgentAvatar({ agent = agents.claude }: { agent?: AgentConfig }) {
  return (
    <span className="flex size-6 shrink-0 items-center justify-center rounded-full border border-(--neutral-250) bg-floating p-px">
      <img
        src={agent.logoUrl}
        alt=""
        width={16}
        height={16}
        className={`size-4 ${agent.invertOnDark ? "dark:invert" : ""}`}
      />
    </span>
  )
}

/** One line of the session; `step` orders its reveal. */
function SessionRow({
  step,
  leading,
  indent = false,
  className = "",
  children,
}: {
  step: number
  leading: ReactNode
  indent?: boolean
  className?: string
  children: ReactNode
}) {
  return (
    <div
      data-session-step
      style={{ "--step": step } as CSSProperties}
      className={`flex min-h-9 items-center gap-2 py-1.5 ${indent ? "px-11" : "px-3"} ${className}`}
    >
      {leading}
      <p className="min-w-0 text-sm leading-5 text-default">{children}</p>
    </div>
  )
}

function ToolChip({ Icon }: { Icon: LucideIcon }) {
  return (
    <span className="flex shrink-0 items-center rounded-full bg-base p-1 shadow-[0_0_0_1px_rgb(0_0_0/0.06)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)]">
      <Icon size={16} strokeWidth={1.33} className="text-muted" aria-hidden="true" />
    </span>
  )
}

export function SessionLinksShowcase() {
  const sceneRef = useRef<HTMLDivElement>(null)
  const sessionRef = useRef<HTMLDivElement>(null)
  const anchorRefs = useRef<Partial<Record<LinkKey, HTMLElement | null>>>({})
  const cardRefs = useRef<Partial<Record<LinkKey, HTMLElement | null>>>({})
  const geometry = useLinks(sceneRef, sessionRef, anchorRefs, cardRefs)

  const [revealed, observe] = useRevealOnce()

  return (
    <div
      ref={observe}
      data-revealed={revealed ? "true" : "false"}
      className="home-new-session-links @container absolute inset-0 flex items-center justify-center p-6 sm:p-16"
      inert
    >
      <div ref={sceneRef} className="relative flex w-full max-w-[722px] flex-col gap-6">
        <div
          ref={sessionRef}
          className={`relative z-10 overflow-clip rounded-lg bg-floating pb-1 font-sans ${elevatedRingClassName}`}
        >
          <p
            data-session-step
            style={{ "--step": 0 } as CSSProperties}
            className="flex min-h-11 items-center px-3 py-2.5 text-sm leading-4 font-semibold text-default"
          >
            Prorate seat changes on annual plans
          </p>
          <SessionRow step={1} leading={<PersonAvatar />}>
            <span ref={(element) => void (anchorRefs.current.search = element)}>
              Bill seats added mid-year right away instead of at renewal.
            </span>
          </SessionRow>
          <SessionRow step={2} leading={<AgentAvatar />} className="hidden @[34rem]:flex">
            I traced seat changes to billing/plan.ts and found where renewals price them.
          </SessionRow>
          <SessionRow
            step={3}
            indent
            leading={<ToolChip Icon={Wrench} />}
            className="hidden @[34rem]:flex"
          >
            6 tool calls
          </SessionRow>
          <SessionRow
            step={4}
            indent
            leading={<ToolChip Icon={Code} />}
            className="hidden @[34rem]:flex"
          >
            <span ref={(element) => void (anchorRefs.current.graph = element)}>
              3 files changed
            </span>
          </SessionRow>
          <SessionRow
            step={5}
            leading={
              <span
                ref={(element) => void (anchorRefs.current.trails = element)}
                className="flex shrink-0"
              >
                <PersonAvatar />
              </span>
            }
          >
            Prorate by day and round down, so nobody gets overcharged.
          </SessionRow>
          <SessionRow step={6} leading={<AgentAvatar />} className="hidden @[34rem]:flex">
            Done. Seat changes now prorate daily, with tests for leap years.
          </SessionRow>
        </div>

        <div className="grid grid-cols-2 gap-4 @[34rem]:grid-cols-3 @[34rem]:gap-6">
          {PRODUCTS.map(({ key, title, Icon, body }, index) => (
            <div
              key={key}
              ref={(element) => void (cardRefs.current[key] = element)}
              data-session-card
              style={{ "--card": index } as CSSProperties}
              className={`relative z-10 flex min-h-[120px] flex-col gap-1 rounded-lg bg-floating px-3 pt-3 pb-4 font-sans ${elevatedRingClassName} ${key === "graph" ? "hidden @[34rem]:flex" : ""}`}
            >
              <div className="flex items-center gap-2">
                <p className="min-w-0 flex-1 text-sm leading-4 font-semibold text-default">
                  {title}
                </p>
                <span className="flex size-6 shrink-0 items-center justify-center">
                  <Icon size={16} strokeWidth={1.33} className="text-muted" aria-hidden="true" />
                </span>
              </div>
              <p className="text-sm leading-5 text-default">{body}</p>
            </div>
          ))}
        </div>

        {geometry && revealed ? (
          <svg
            className="pointer-events-none absolute inset-0 z-0 overflow-visible text-white/60"
            width={geometry.width}
            height={geometry.height}
            viewBox={`0 0 ${geometry.width} ${geometry.height}`}
            aria-hidden="true"
          >
            {geometry.links.map(({ key, d }, index) => (
              <g key={key}>
                <mask id={`home-new-session-link-${key}`} maskUnits="userSpaceOnUse">
                  <path
                    d={d}
                    pathLength={1}
                    stroke="white"
                    strokeWidth={4}
                    fill="none"
                    className="home-new-mirror-draw"
                    style={{ animationDelay: `${900 + index * 220}ms`, animationDuration: "700ms" }}
                  />
                </mask>
                <path
                  d={d}
                  fill="none"
                  stroke="currentColor"
                  strokeDasharray="4 4"
                  strokeLinejoin="round"
                  mask={`url(#home-new-session-link-${key})`}
                  className="home-new-mirror-dash"
                />
              </g>
            ))}
          </svg>
        ) : null}
      </div>
    </div>
  )
}
